/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

interface BridgeMeta {
  timestamp?: number;
  source?: 'web' | 'native';
  replyTo?: string;
}

interface BridgeMessage<T = any> {
  id: string;
  type: 'request' | 'response' | 'event' | 'error' | 'handshake';
  action: string;
  payload?: T;
  meta?: BridgeMeta;
}

interface Pending<T = any> {
  resolve: (data: T) => void;
  reject: (err: any) => void;
}

interface IComeAppBridge {
  getJSI(): ((json: string) => void | undefined);
  invoke<T = any>(action: string, payload?: any): Promise<T>;
  register(action: string, handler: (payload: any, msg?: BridgeMessage) => any): void;
  unregister(action: string, handler: (payload: any, msg?: BridgeMessage) => any): void;
  once(action: string, handler: (payload: any, msg?: BridgeMessage) => any): void;
  emit(action: string, e?: any): void;
}

export enum BridgeCode {
  SUCCESS = 0, // 调用成功
  UNKNOWN_ERROR = 1, // 未知错误
  INVALID_ACTION = 2, // 未定义的 action
  INVALID_PARAMS = 3, // 参数格式不对
  UNAUTHORIZED = 401, // 未鉴权 / token 校验失败
  FORBIDDEN = 403, // 禁止访问（权限不足）
  TIMEOUT = 408, // 调用超时
  NOT_IMPLEMENTED = 501, // Native 未实现该接口
  INTERNAL_ERROR = 500, // Native 内部错误
}

const bridgeName = 'ComeAppBridge';
const JavaScriptInterfaceName = 'comeAppWebView';

export class ComeAppBridge implements IComeAppBridge {
  private static instance: ComeAppBridge;
  private pending: Map<string, Pending> = new Map();
  private timeout = 8000; // 默认 8 秒超时
  private idCounter = 0;

  private constructor() {
    // 监听来自 React Native 的消息
    window.addEventListener("message", this._handleMessage.bind(this));
    (window as any)[`__${bridgeName}__`] = this;
  }

  public static getInstance(): ComeAppBridge {
    if (!ComeAppBridge.instance) {
      ComeAppBridge.instance = new ComeAppBridge();
    }
    return ComeAppBridge.instance;
  }

  private sendToNative(message: BridgeMessage) {
    if (typeof (window as any)[JavaScriptInterfaceName]?.postMessage === 'function') {
      (window as any)[JavaScriptInterfaceName]?.postMessage(JSON.stringify(message));
    } else {
      console.error(`${JavaScriptInterfaceName} not found`);
    }
  }

  public getJSI(): ((json: string) => void | undefined) {
    return (window as any)[JavaScriptInterfaceName]?.postMessage;
  }

  /**
   * 调用 Native 方法
   */
  public invoke<T = any>(action: string, payload?: any): Promise<T> {
    // 非 App 环境（无 JSI），不向 Native 发起调用，直接返回空字符串，避免 8 秒超时 reject
    if (typeof this.getJSI() !== 'function') {
      return Promise.resolve('' as T);
    }
    const req = this.createMessage('request', action, payload);
    const id = req.id;
    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(id);
        reject({ code: BridgeCode.TIMEOUT, message: 'timeout' });
      }, this.timeout);
      this.pending.set(id, {
        resolve: (data) => {
          clearTimeout(timer);
          resolve(data);
        },
        reject: (err) => {
          clearTimeout(timer);
          reject(err);
        },
      });
      this.sendToNative(req);
    });
  }

  /**
   * Native 调用 JS 方法（H5 可以注册事件给 Native 调用）
   */
  private handlers: Record<string, (payload: any, msg?: BridgeMessage) => any> =
    {};

  private callbacks: Record<string, ((payload: any, msg?: BridgeMessage) => any)[]> = {};

  public register(action: string, handler: (payload: any, msg?: BridgeMessage) => any) {
    if (!this.callbacks[action]) {
      this.callbacks[action] = [];
    }
    this.callbacks[action].push(handler);
  }

  public unregister(action: string, handler: (payload: any, msg?: BridgeMessage) => any) {// 保留后续扩展
    if (!this.callbacks[action]) return;
    this.callbacks[action] = this.callbacks[action].filter(h => h !== handler);
  }

  public once(action: string, handler: (payload: any, msg?: BridgeMessage) => any) {
    const wrapper = (payload: any, msg?: BridgeMessage) => {
      handler(payload, msg);
      this.unregister(action, wrapper);
    };
    this.register(action, wrapper);
  }

  public emit(action: string, payload?: any) {
    const event = this.createMessage('event', action, payload);
    this.sendToNative(event);
  }

  /**
   * 内部：处理来自 Native 的消息
   */
  private _handleMessage(event: MessageEvent) {
    let res: BridgeMessage;
    try {
      res =
        typeof event.data === "string" ? JSON.parse(event.data) : event.data;
    } catch {
      console.warn('invalid msg from web', event.data);
      return;
    }

    const { type, id, action, meta } = res;

    if (type === 'response' || type === 'error') {
      const replyTo = meta?.replyTo || id;
      const p = this.pending.get(replyTo);
      if (p) {
        this.pending.delete(replyTo);
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        type === 'response' ? p.resolve(res.payload) : p.reject(res.payload);
      }
      return;
    }

    if (type === 'request') {
      const h = this.handlers[action];
      if (!h) {
        this.sendToNative(this.createMessage('error', action, { code: BridgeCode.INVALID_ACTION, message: `No handler for action: ${action}` }, { replyTo: id }));
        return;
      }

      Promise.resolve(h(res.payload, res))
        .then((data) => {
          this.sendToNative(this.createMessage('response', action, data, { replyTo: id }));
        })
        .catch((err) => {
          this.sendToNative(this.createMessage('error', action, { code: BridgeCode.INTERNAL_ERROR, message: err.message || 'Handler error' }, { replyTo: id }));
        });

      return;
    }

    if (type === 'event') {
      const handlers = this.callbacks[action];
      if (handlers && handlers.length > 0) {
        handlers.forEach(h => {
          try {
            h(res.payload, res);
          } catch (error) {
            console.warn(`Error handling event ${action}`, error);
          }
        });
      }
    }

  }

  private _genId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substring(2) + this.idCounter++;
  }

  private createMessage(type: BridgeMessage['type'], action: string, payload?: any, meta?: BridgeMeta): BridgeMessage {
    return {
      id: this._genId(),
      type,
      action,
      payload,
      meta: {
        timestamp: Date.now(),
        source: 'web',
        ...meta,
      },
    };
  }
}

const fn: (...args: any[]) => any = (_) => _;

export const bridge = typeof window !== 'undefined' ? ComeAppBridge.getInstance() : {
  getJSI: fn,
  invoke: fn,
  emit: fn,
  register: fn,
  unregister: fn
} as IComeAppBridge;
